import { Component, DestroyRef, TemplateRef, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ProjectLocationItemComponent } from '@app/shared/components/project-location-item/project-location-item.component';
import { MenuActionDirective } from '@app/shared/directives/menu-action.directive';
import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { LocationsActionsService } from '@app/shared/services/locations-actions.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { Location } from '@app/shared/types/models/location/location.model';
import {
  ProffeoUiSortableTreeContentContext,
  ProffeoUiSortableTreeReorderEvent,
  ProffeoUiTreeNode,
  UiButtonComponent,
  UiSortableTreeComponent
} from '@app/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-project-location',
  imports: [
    MenuActionDirective,
    MatButtonModule,
    MatIconModule,
    ProjectLocationItemComponent,
    TranslatePipe,
    MatMenuModule,
    UiButtonComponent,
    UiSortableTreeComponent
  ],
  templateUrl: './project-location.component.html',
  styleUrl: './project-location.component.scss'
})
export class ProjectLocationComponent {
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);
  private readonly locationsActionsService: LocationsActionsService = inject(LocationsActionsService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly locationContentTemplate =
    viewChild<TemplateRef<ProffeoUiSortableTreeContentContext<Location>>>('locationContentTemplate');

  protected readonly projectName = this.activeProjectService.activeProjectName;
  protected readonly locations = this.locationsActionsService.getProjectLocations();
  protected readonly editableLocations = this.locationsActionsService.getEditableProjectLocations();
  protected readonly selectedLocations = signal<Location[]>([]);
  protected readonly locationNodes = computed((): ProffeoUiTreeNode<Location>[] => {
    const roots = this.locations() ?? [];
    return roots
      .filter((location): location is Location & { id: string } => !!location.id)
      .map(location => this.toTreeNode(location));
  });
  protected readonly locationContent = computed(
    (): TemplateRef<ProffeoUiSortableTreeContentContext<Location>> | null => this.locationContentTemplate() ?? null
  );

  protected compareLocations = (first: Location, second: Location): boolean => first.id === second.id;

  protected isLocationEditable(location: Location): boolean {
    return !location.readOnly;
  }

  protected reorderLocations(event: ProffeoUiSortableTreeReorderEvent<Location>): void {
    const orderedIds = event.rows.map(location => location.id).filter((id): id is string => !!id);
    this.locationsActionsService
      .reorderLocations(orderedIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.toastService.success(this.translateService.instant('project-locations.toasts.successfully-reordered'))
      );
  }

  protected openAddLocationDialog(): void {
    this.locationsActionsService.openAddLocationDialog();
  }

  protected openAddMultipleLocationDialog(): void {
    this.locationsActionsService.openAddMultipleLocationDialog();
  }

  protected openDuplicateProjectLocationDialog(): void {
    this.locationsActionsService.openDuplicateLocationDialog({
      locationsToDuplicate: this.selectedLocations(),
      locations: this.editableLocations
    });
  }

  private toTreeNode(location: Location & { id: string }): ProffeoUiTreeNode<Location> {
    const children = (this.locationsActionsService.getLocationsByParent(location.id)() ?? []).filter(
      (child): child is Location & { id: string } => !!child?.id
    );

    return {
      id: location.id,
      label: location.name ?? '',
      value: location,
      disabled: location.readOnly,
      children: children.map(child => this.toTreeNode(child))
    };
  }
}
